import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, Clock, ArrowRight } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { logError } from "@/lib/logger";

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 5;

export default function PaymentReturn() {
    const [params] = useSearchParams();
    const orderId = params.get("order_id");
    const { refresh } = useAuth();
    const [state, setState] = useState({ status: "checking", message: "Confirming your payment..." });

    useEffect(() => {
        if (!orderId) {
            setState({ status: "error", message: "Missing order reference. Please contact support." });
            return;
        }
        let polls = 0;
        let cancelled = false;
        async function check() {
            polls += 1;
            try {
                const { data } = await api.get(`/payments/verify?order_id=${encodeURIComponent(orderId)}`);
                if (cancelled) return;
                if (data.status === "paid") {
                    setState({ status: "paid", message: `Your ${data.plan_id?.toUpperCase()} plan is now active. Thank you!`, plan: data.plan_id });
                    refresh();
                    return;
                }
                if (data.status === "failed") {
                    setState({ status: "failed", message: "Payment was not completed. You can try again from the Pricing page." });
                    return;
                }
                if (polls < MAX_POLLS) {
                    setTimeout(check, POLL_INTERVAL_MS);
                } else {
                    setState({ status: "pending", message: "Payment is still being processed. We'll email you a confirmation shortly." });
                }
            } catch (e) {
                logError("verify failed", e);
                if (!cancelled) setState({ status: "error", message: formatErr(e) });
            }
        }
        check();
        return () => { cancelled = true; };
    }, [orderId, refresh]);

    return (
        <section className="min-h-screen pt-40 pb-16 mesh-gradient flex items-center justify-center" data-testid="payment-return-page">
            <div className="max-w-md w-full mx-4">
                <div className="glass rounded-3xl p-8 text-center">
                    <StatusIcon status={state.status} />
                    <h1 className="font-display font-bold text-2xl mt-5 mb-2">{TITLES[state.status]}</h1>
                    <p className="text-gray-400 text-sm mb-6">{state.message}</p>
                    <CtaButtons status={state.status} />
                </div>
            </div>
        </section>
    );
}

const TITLES = {
    checking: "Confirming payment...",
    paid: "Payment Successful 🎉",
    failed: "Payment Failed",
    pending: "Still processing",
    error: "Something went wrong",
};

function StatusIcon({ status }) {
    if (status === "paid") return <CheckCircle className="w-16 h-16 mx-auto text-brand-400" />;
    if (status === "failed" || status === "error") return <XCircle className="w-16 h-16 mx-auto text-red-400" />;
    if (status === "pending") return <Clock className="w-16 h-16 mx-auto text-amber-400" />;
    return <div className="w-14 h-14 mx-auto rounded-full border-4 border-brand-500/30 border-t-brand-500 animate-spin" />;
}

function CtaButtons({ status }) {
    if (status === "paid") {
        return (
            <Link to="/dashboard" className="btn-primary inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white" data-testid="payment-go-dashboard">
                Go to Dashboard <ArrowRight className="w-4 h-4" />
            </Link>
        );
    }
    if (status === "failed" || status === "error") {
        return (
            <Link to="/pricing" className="btn-primary inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white" data-testid="payment-try-again">
                Try Again
            </Link>
        );
    }
    if (status === "pending") {
        return (
            <Link to="/pricing" className="glass inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold hover:bg-white/10" data-testid="payment-back-pricing">
                Back to Pricing
            </Link>
        );
    }
    return null;
}
